import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { AuthUser } from '@/lib/auth';
import * as auth from '@/lib/auth';

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  setUser: (user: AuthUser | null) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUserState] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const setUser = useCallback((u: AuthUser | null) => {
    setUserState(u);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const { user: u } = await auth.login({ username, password });
    setUserState(u);
  }, []);

  const logout = useCallback(() => {
    auth.logout();
    setUserState(null);
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    await auth.changePassword(currentPassword, newPassword);
  }, []);

  useEffect(() => {
    const token = auth.getStoredToken();
    if (!token) {
      setLoading(false);
      return;
    }
    auth.verifyToken(token).then((u) => {
      setUserState(u);
      setLoading(false);
    }).catch(() => {
      auth.clearStoredToken();
      setLoading(false);
    });
  }, []);

  const value: AuthContextValue = {
    user,
    loading,
    login,
    logout,
    changePassword,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
